/**
 * Hier liegt das Template, dass für Facebookposts im Bereich Overview genutzt wird
 */

import React, { useEffect, useState, useCallback } from 'react'

import { Box, Grid, Paper, Stack, Theme, Typography, Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles'

import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'

import {
  ActionCall,
  ScreenEnum, ViewEnum,
} from '@professocial/professocialTypes/types/commonTypes'
import { FacebookPost, GetFacebookFeedResponse } from '@3m5/sm_publish-frontend-api'

import ProfileImage from '../../../shared/components/ModuleComponents/Post/modules/ProfileImage'
import LinkHeadline from '../../../shared/components/ModuleComponents/Post/modules/LinkHeadline'
import SubHeadline from '../../../shared/components/ModuleComponents/Post/modules/SubHeadline'
import Like from '../../../shared/components/ModuleComponents/Post/modules/Like'
import DeletePost from '../../../shared/components/ModuleComponents/Post/modules/DeletePost'
import PostContent from '../../../shared/components/ModuleComponents/Post/modules/PostContent'
import Statistic from '../../../shared/components/ModuleComponents/Post/modules/Statistic'
import NewComment from '../../../shared/components/ModuleComponents/Post/modules/Comments/NewComment'
import Comments from '../../../shared/components/ModuleComponents/Post/modules/Comments/Comments'

import getDateFormat from '../../../defaults/getDateFormat'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import { IFacebookPostProps } from './FacebookPostTemplate'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { PostingRootState } from '../../../store/postingRoot.reducer'
import { namedRequestData } from '../../../store/common/common.selectors'
import { deleteFacebookPostCommentInitiated, deletePostInitiated, getFacebookFeedForPageInitiated } from '../../../store/facebook/facebook.actions'
import { GetFacebookFeedForPageRequest, NetworkType } from '@3m5/sm_publish-frontend-api'
import { getFacebookFeeds } from '../../../store/facebook/facebook.selector'
import { clearRequests } from '../../../store/common/common.actions'

const useStyles = makeStyles((theme: Theme) => ({
  postContainer: {
    padding: theme.spacing(4),
  },
  commentsContainer: {
    padding: theme.spacing(2, 4, 4, 4),
    [theme.breakpoints.up('md')]: { padding: theme.spacing(4, 3) },
    backgroundColor: '#F9F9F9',
  },
  commentsWrapper: {
    overflowY: 'scroll',
    height: '100%',
    maxHeight: '500px',
    '&::-webkit-scrollbar': { display: 'none' },
    '&::-moz-scrollbar': { display: 'none' },
    scrollbarWidth: 'none',
  },
  noComments: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  container: {
    position: 'relative',
  },
  backgroundLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgb(255,255,255,0.6)',
  },
  preloader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
  },
}))

const FacebookOverview: React.FC<IFacebookPostProps> = (props: IFacebookPostProps) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()

  const view = props.view || ViewEnum.overview
  const size = props.size || ScreenEnum.xl

  const dispatchGetFacebookFeeds = useCallback((params: { data: GetFacebookFeedForPageRequest, id: string }) => dispatch(getFacebookFeedForPageInitiated(params)), [dispatch])
  const dispatchClearRequests = useCallback(() => dispatch(clearRequests()), [dispatch])

  const actionOnDeletePost: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, deletePostInitiated.type))
  const actionOnDeleteComment: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, deleteFacebookPostCommentInitiated.type))
  const storedFacebookData: GetFacebookFeedResponse | undefined = useSelector(getFacebookFeeds)
  const statusOnFeedLoad: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, getFacebookFeedForPageInitiated.type))

  const [post, setPost] = useState<FacebookPost>(props.post as FacebookPost)

  const networkType: NetworkType = props.networkType || NetworkType.Facebook

  const handleNewCommentAdded = (value: any) => {
    if (props.networkPageId) {
      const params: GetFacebookFeedForPageRequest = {
        networkPageId: props.networkPageId,
      }
      dispatchGetFacebookFeeds({ data: params, id: post.networkPostId })
    }
  }

  const handleDeleteComment = (id: string | undefined) => {
    id && setPost({ ...post, comments: post.comments?.filter(comment => comment.networkCommentId !== id) })
  }

  const handleDeletePost = () => {
    dispatchClearRequests()
    props.onEditFeed && props.onEditFeed(post)
  }

  useEffect(() => {
    actionOnDeleteComment?.status === 'success' && handleDeleteComment(actionOnDeleteComment?.id)
  }, [actionOnDeleteComment])

  useEffect(() => {
    actionOnDeletePost?.status === 'success' && handleDeletePost()
  }, [actionOnDeletePost])

  useEffect(() => {
    setPost(props.post as FacebookPost)
  }, [props.post])

  useEffect(() => {
    if (storedFacebookData) {
      const postWithNewComments = storedFacebookData.posts.find(item => item.networkPostId === post.networkPostId)
      postWithNewComments && setPost(postWithNewComments)
    }
  }, [storedFacebookData])

  return (
    <div className={classes.container}>
      <Paper sx={{ mb: 1 }}>
        <Grid container>
          <Grid className={classes.postContainer} xs={12} md={7} lg={8} container item>
            <Grid item xs='auto'>
              <ProfileImage
                data={`https://graph.facebook.com/${post.postedByNetworkId}/picture`}
                view={props.view}
              />
            </Grid>
            <Grid xs item>
              <Stack
                direction='row'
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
              >
                <Stack direction='column'>
                  <LinkHeadline
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={networkType}
                    value={{
                      url: `https://www.facebook.com/${post.networkPostId}`,
                      label: post.postedByNetworkName,
                    }}
                    view={view}
                    size={size}
                  />
                  <SubHeadline
                    networkPageId={props.networkPageId}
                    post={post}
                    value={`${t('postCommon:published')} ${post.postedAt && formatDate(post.postedAt.getTime(), getDateFormat('dateTime'))}`}
                  />
                </Stack>
                <Box marginLeft='auto' marginRight='20px'>
                  {post.likedByOwner !== undefined && props.networkPageId &&
                    <Like
                      networkPageId={props.networkPageId}
                      post={post}
                      networkType={networkType}
                      value={post.likedByOwner}
                    />}
                </Box>
                <Box>
                  <DeletePost
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={networkType}
                    value={post.networkPostId}
                  />
                </Box>
              </Stack>
              <Grid xs={12} item>
                <PostContent
                  networkPageId={props.networkPageId}
                  post={post}
                  postType={props.postType}
                  networkType={networkType}
                  value={post}
                  view={view}
                  size={size}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid className={classes.commentsContainer} xs={12} md={5} lg={4} container item>
            <Grid xs item>
              <Stack direction='column' spacing={2} height='100%' sx={{ paddingTop: '9px' }}>
                <Statistic
                  networkPageId={props.networkPageId}
                  post={post}
                  view={ViewEnum.single}
                  networkType={networkType}
                  value={{
                    postImpressionsUnique: post?.postStatistics?.postImpressionsUnique ?? 0,
                    postImpressions: post?.postStatistics?.postImpressions ?? 0,
                    postClicks: post?.postStatistics?.postClicks ?? 0,
                    postReactionsLikeTotal: post?.postStatistics?.postReactionsLikeTotal ?? 0,
                    shares: post?.postStatistics?.shares ?? 0,
                    commentCount: post?.postStatistics?.commentCount ?? 0,
                  }}
                />
                <NewComment
                  networkPageId={props.networkPageId}
                  post={post}
                  networkType={networkType}
                  onChange={handleNewCommentAdded}
                />
                <Box className={classes.commentsWrapper}>
                  {statusOnFeedLoad?.status === 'pending' && statusOnFeedLoad?.id === post.networkPostId && (
                    <Grid container item xs={12} sx={{ pt: 3 }}>
                      <Grid container spacing={1}>
                        <Grid item xs='auto'>
                          <Skeleton sx={{ mr: '13px' }} variant='circular' width={40} height={40} />
                        </Grid>
                        <Grid xs container item>
                          <Stack direction='row' alignItems='center' width='100%'>
                            <Box width='100%'>
                              <Stack direction='column'>
                                <Skeleton width={60} height={20} />
                                <Skeleton width={100} height={20} />
                              </Stack>
                              <Skeleton width='100%' height={40} variant='rectangular' sx={{ mt: '5px' }} />
                            </Box>
                            <Box marginLeft='auto' width='50px' />
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  {post.comments?.length === 0 && statusOnFeedLoad?.status !== 'pending' ? (
                    <Box className={classes.noComments}>
                      <Typography variant='body1'>{t('postCommon:noCommentsYet')}</Typography>
                    </Box>
                  ) : (
                    <>
                      <Comments
                        networkPageId={props.networkPageId}
                        post={post}
                        networkType={networkType}
                        value={post}
                      />
                    </>)}
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      {actionOnDeletePost?.inProgress && actionOnDeletePost?.id === post.networkPostId &&
        <>
          <div className={classes.backgroundLayer} />
          <div className={classes.preloader}><Preloader color='secondary' /></div>
        </>}
    </div>
  )
}

export default FacebookOverview
